import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Save, Trash2, Sparkles, Plus, Bot, Loader2, Globe, FileText, RefreshCw, ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import SeoSnippetPreview from './seo/SeoSnippetPreview';

const EMPTY_PAGE = {
  route: '/',
  title: '',
  description: '',
  keywords: '',
  og_image: '',
  og_type: 'website',
  twitter_card: 'summary_large_image',
  canonical: '',
  no_index: false,
};

export default function SeoPanel() {
  const [pages, setPages] = useState([]);
  const [robots, setRobots] = useState({ template: '', bot_user_agents: [], bot_rate_limit: { enabled: true, requests_per_minute: 30 } });
  const [activeRoute, setActiveRoute] = useState('/');
  const [draft, setDraft] = useState(EMPTY_PAGE);
  const [newRoute, setNewRoute] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [ogImageBusy, setOgImageBusy] = useState(false);
  const [bulkRegen, setBulkRegen] = useState({ running: false, done: 0, total: 0 });
  const [pageSummary, setPageSummary] = useState('');
  const [audience, setAudience] = useState('novelists and serious writers');
  const [botUaInput, setBotUaInput] = useState('');

  const reload = async () => {
    try {
      const [seoRes, robotsRes] = await Promise.all([adminApi.listSeo(), adminApi.getRobots()]);
      const list = seoRes.pages || [];
      setPages(list);
      setRobots({
        template: robotsRes.template || '',
        bot_user_agents: robotsRes.bot_user_agents || [],
        bot_rate_limit: robotsRes.bot_rate_limit || { enabled: true, requests_per_minute: 30 },
      });
      const route = activeRoute || (list[0]?.route ?? '/');
      const hit = list.find((p) => p.route === route) || list[0] || EMPTY_PAGE;
      setActiveRoute(hit.route);
      setDraft({ ...EMPTY_PAGE, ...hit });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load SEO config.');
    }
  };

  // iter55f58c — mount-only reload; `reload` is a stable arrow.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const selectRoute = (route) => {
    const hit = pages.find((p) => p.route === route);
    setActiveRoute(route);
    setDraft({ ...EMPTY_PAGE, ...(hit || { route }) });
  };

  const savePage = async () => {
    setBusy(true);
    try {
      const { route, ...patch } = draft;
      await adminApi.saveSeoRoute(route, patch);
      toast.success(`Saved meta for ${route}.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const deletePage = async () => {
    if (draft.route === '/') { toast.error("Can't delete the root SEO entry."); return; }
    if (!confirm(`Delete SEO for ${draft.route}?`)) return;
    setBusy(true);
    try {
      await adminApi.deleteSeoRoute(draft.route);
      toast.success(`Deleted ${draft.route}.`);
      setActiveRoute('/');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    } finally { setBusy(false); }
  };

  const addRoute = async () => {
    const r = newRoute.trim();
    if (!r) return;
    const route = r.startsWith('/') ? r : '/' + r;
    setNewRoute('');
    setActiveRoute(route);
    setDraft({ ...EMPTY_PAGE, route });
  };

  const suggestWithAi = async () => {
    setSuggestBusy(true);
    try {
      const r = await adminApi.suggestSeo(draft.route, pageSummary, audience);
      const s = r.suggestion || {};
      setDraft((d) => ({
        ...d,
        title: s.title || d.title,
        description: s.description || d.description,
        keywords: s.keywords || d.keywords,
      }));
      toast.success('AI suggestion ready — review then click Save.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suggestion failed.');
    } finally { setSuggestBusy(false); }
  };

  const regenerateOgImage = async () => {
    setOgImageBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const encoded = encodeURIComponent(draft.route.replace(/^\//, ''));
      const { data } = await axios.post(
        `${API}/api/admin/seo/${encoded}/regenerate-og-image`,
        {},
        { withCredentials: true },
      );
      setDraft((d) => ({ ...d, og_image: data.og_image }));
      toast.success('OG image regenerated — review then click Save to keep it.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Image generation failed.');
    } finally {
      setOgImageBusy(false);
    }
  };

  // Sweep all routes that have no og_image, generate one for each sequentially.
  const bulkRegenerateOgImages = async () => {
    const missing = pages.filter((p) => !p.og_image);
    if (!missing.length) { toast.info('All routes already have an OG image.'); return; }
    setBulkRegen({ running: true, done: 0, total: missing.length });
    const API = process.env.REACT_APP_BACKEND_URL;
    let done = 0;
    for (const page of missing) {
      try {
        const encoded = encodeURIComponent(page.route.replace(/^\//, ''));
        const { data } = await axios.post(
          `${API}/api/admin/seo/${encoded}/regenerate-og-image`,
          {},
          { withCredentials: true },
        );
        done += 1;
        setBulkRegen((s) => ({ ...s, done }));
        // Update the pages list in-place so the route list shows the new image immediately
        setPages((prev) => prev.map((p) =>
          p.route === page.route ? { ...p, og_image: data.og_image } : p,
        ));
        // If this is the currently-active route, refresh the draft form too
        if (activeRoute === page.route) {
          setDraft((d) => ({ ...d, og_image: data.og_image }));
        }
      } catch {
        // Skip failed routes — log silently and continue
        done += 1;
        setBulkRegen((s) => ({ ...s, done }));
      }
    }
    setBulkRegen({ running: false, done: 0, total: 0 });
    toast.success(`OG images generated for ${done} route${done !== 1 ? 's' : ''}.`);
    await reload();
  };

  const saveRobots = async () => {    setBusy(true);
    try {
      await adminApi.saveRobots({
        template: robots.template,
        bot_user_agents: robots.bot_user_agents,
        bot_rate_limit: robots.bot_rate_limit,
      });
      toast.success('Robots & bot policy saved.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const addBotUa = () => {
    const ua = botUaInput.trim();
    if (!ua) return;
    if (robots.bot_user_agents.includes(ua)) { toast.info('Already in the list.'); return; }
    setRobots((r) => ({ ...r, bot_user_agents: [...r.bot_user_agents, ua] }));
    setBotUaInput('');
  };

  const removeBotUa = (ua) => {
    setRobots((r) => ({ ...r, bot_user_agents: r.bot_user_agents.filter((x) => x !== ua) }));
  };

  return (
    <section data-testid="admin-seo-panel">
      <PanelHeader
        title="SEO & Bot Traffic"
        subtitle="Manage per-route meta tags, robots.txt, and the bot rate-limit policy. Public crawlers read /robots.txt and the home <Helmet> consumes /api/seo on first paint."
        onRefresh={reload}
        right={
          <div className="flex items-center gap-2">
            {bulkRegen.running ? (
              <div className="flex items-center gap-1.5 rounded border border-border bg-muted/60 px-2.5 py-1 text-[11px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Generating {bulkRegen.done} / {bulkRegen.total}…</span>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-[11.5px]"
                onClick={bulkRegenerateOgImages}
                disabled={bulkRegen.running}
                title="Generate AI OG images for every route currently missing one"
                data-testid="seo-bulk-regen-btn"
              >
                <ImageIcon className="h-3.5 w-3.5" />
                Fill missing OG images
              </Button>
            )}
          </div>
        }
      />

      {/* ============ Per-route meta tags ============ */}
      <div className="mt-4 grid gap-4 lg:grid-cols-[260px_1fr]">
        <div className="rounded-md border border-border bg-card p-3">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Routes</div>
          </div>
          <ul className="mt-2 space-y-0.5">
            {pages.map((p) => (
              <li key={p.route}>
                <button
                  data-testid={`seo-route-${p.route.replace(/\//g, '-') || 'root'}`}
                  onClick={() => selectRoute(p.route)}
                  className={`w-full text-left rounded-sm px-2 py-1 text-[12px] hover:bg-muted ${activeRoute === p.route ? 'bg-muted font-semibold' : ''}`}
                >
                  {p.route}
                  {p.no_index && <span className="ml-1 text-[10px] text-warning-foreground">[noindex]</span>}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-border pt-3">
            <Label className="text-[11px]">Add route</Label>
            <div className="mt-1 flex gap-1">
              <Input
                value={newRoute}
                onChange={(e) => setNewRoute(e.target.value)}
                placeholder="/pricing"
                className="h-8 text-[12px]"
                data-testid="seo-new-route-input"
                onKeyDown={(e) => e.key === 'Enter' && addRoute()}
              />
              <Button size="sm" onClick={addRoute} className="h-8 px-2" data-testid="seo-new-route-add">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[12.5px] font-semibold">Editing <span className="font-mono-stat text-primary">{draft.route}</span></div>
              <div className="text-[11px] text-muted-foreground">Title ≤ 60 chars, description ≤ 155 chars (Google snippet limits).</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={savePage} disabled={busy} className="h-8 gap-1.5" data-testid="seo-save-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </Button>
              {draft.route !== '/' && (
                <Button size="sm" variant="ghost" onClick={deletePage} disabled={busy} className="h-8 gap-1.5 text-destructive" data-testid="seo-delete-btn">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              )}
            </div>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label className="text-[11px]">Title <span className="text-muted-foreground">({draft.title.length}/60)</span></Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="mt-1 h-8 text-[12px]" data-testid="seo-title-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Description <span className="text-muted-foreground">({draft.description.length}/155)</span></Label>
              <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} className="mt-1 text-[12px]" data-testid="seo-description-input" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px]">Keywords (comma-separated)</Label>
              <Input value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} className="mt-1 h-8 text-[12px]" data-testid="seo-keywords-input" />
            </div>
            <div>
              <Label className="text-[11px]">OG image URL</Label>
              <div className="mt-1 flex gap-1.5">
                <Input
                  value={draft.og_image}
                  onChange={(e) => setDraft({ ...draft, og_image: e.target.value })}
                  placeholder="https://…/og.png"
                  className="h-8 flex-1 font-mono-stat text-[11.5px]"
                  data-testid="seo-og-image-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 shrink-0 gap-1.5 px-2"
                  onClick={regenerateOgImage}
                  disabled={ogImageBusy}
                  title="Generate a new AI OG image for this route using the page title + description"
                  data-testid="seo-og-image-regenerate"
                >
                  {ogImageBusy
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <><RefreshCw className="h-3.5 w-3.5" /><span className="hidden sm:inline text-[11px]">Regenerate</span></>}
                </Button>
              </div>
              {draft.og_image && (
                <div className="mt-1.5 flex items-center gap-2">
                  <img
                    src={draft.og_image}
                    alt="OG preview"
                    className="h-[42px] w-[80px] rounded border border-border object-cover"
                    data-testid="seo-og-image-preview"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <span className="text-[10px] text-muted-foreground">Preview (1200 × 630)</span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-[11px]">Canonical URL (optional)</Label>
              <Input value={draft.canonical} onChange={(e) => setDraft({ ...draft, canonical: e.target.value })} placeholder="auto" className="mt-1 h-8 font-mono-stat text-[11.5px]" data-testid="seo-canonical-input" />
            </div>
            <div>
              <Label className="text-[11px]">OG type</Label>
              <Input value={draft.og_type} onChange={(e) => setDraft({ ...draft, og_type: e.target.value })} className="mt-1 h-8 text-[12px]" />
            </div>
            <div>
              <Label className="text-[11px]">Twitter card</Label>
              <Input value={draft.twitter_card} onChange={(e) => setDraft({ ...draft, twitter_card: e.target.value })} className="mt-1 h-8 text-[12px]" />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Switch
                checked={Boolean(draft.no_index)}
                onCheckedChange={(v) => setDraft({ ...draft, no_index: v })}
                data-testid="seo-noindex-switch"
              />
              <Label className="text-[12px]">Block search engines (no-index) for this route</Label>
            </div>
          </div>

          <SeoSnippetPreview
            siteUrl={typeof window !== 'undefined' ? window.location.origin : ''}
            route={draft.route}
            title={draft.title}
            description={draft.description}
            noIndex={draft.no_index}
          />

          {/* ----- AI suggest ----- */}
          <div className="mt-4 rounded-md border border-dashed border-border bg-muted/30 p-3">
            <div className="flex items-center gap-2 text-[12.5px] font-semibold"><Sparkles className="h-3.5 w-3.5 text-primary" /> Suggest with Claude</div>
            <p className="text-[11px] text-muted-foreground">Describe what this page is about; we&apos;ll draft a title, description, and keywords (review before saving).</p>
            <div className="mt-2 grid gap-2 md:grid-cols-[1fr_220px]">
              <Textarea
                value={pageSummary}
                onChange={(e) => setPageSummary(e.target.value.slice(0, 4000))}
                rows={2}
                placeholder="e.g. Landing page for The Loom V2.38_f55 — explains multi-model AI, character bibles, and Stripe token pricing."
                className="text-[12px]"
                data-testid="seo-suggest-summary"
              />
              <div className="flex flex-col gap-2">
                <Input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="Target audience"
                  className="h-8 text-[12px]"
                  data-testid="seo-suggest-audience"
                />
                <Button size="sm" onClick={suggestWithAi} disabled={suggestBusy} className="h-8 gap-1.5" data-testid="seo-suggest-btn">
                  {suggestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {suggestBusy ? 'Drafting…' : 'Suggest tags'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ Robots & bot policy ============ */}
      <div className="mt-6 rounded-md border border-border bg-card p-4" data-testid="admin-robots-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[12.5px] font-semibold flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> robots.txt template</div>
            <div className="text-[11px] text-muted-foreground">Use <code>{'{site_url}'}</code> to interpolate your public host. Served at <code>/robots.txt</code>.</div>
          </div>
          <Button size="sm" onClick={saveRobots} disabled={busy} className="h-8 gap-1.5" data-testid="seo-robots-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save robots & policy
          </Button>
        </div>

        <Textarea
          value={robots.template}
          onChange={(e) => setRobots({ ...robots, template: e.target.value })}
          rows={10}
          className="mt-3 font-mono-stat text-[11.5px]"
          data-testid="seo-robots-textarea"
        />

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div>
            <Label className="text-[12px] flex items-center gap-1.5"><Bot className="h-3.5 w-3.5" /> Throttled bot user-agents</Label>
            <p className="text-[11px] text-muted-foreground">Requests matching any UA below are rate-limited by the bot middleware.</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {robots.bot_user_agents.map((ua) => (
                <span key={ua} className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 text-[11px]">
                  {ua}
                  <button onClick={() => removeBotUa(ua)} className="text-destructive hover:text-destructive/80" aria-label={`Remove ${ua}`} data-testid={`seo-bot-remove-${ua}`}>×</button>
                </span>
              ))}
              {robots.bot_user_agents.length === 0 && <span className="text-[11px] text-muted-foreground">No bots configured.</span>}
            </div>
            <div className="mt-2 flex gap-1">
              <Input
                value={botUaInput}
                onChange={(e) => setBotUaInput(e.target.value)}
                placeholder="GPTBot"
                className="h-8 text-[12px]"
                data-testid="seo-bot-ua-input"
                onKeyDown={(e) => e.key === 'Enter' && addBotUa()}
              />
              <Button size="sm" onClick={addBotUa} className="h-8 px-2" data-testid="seo-bot-ua-add"><Plus className="h-3.5 w-3.5" /></Button>
            </div>
          </div>

          <div className="rounded-md border border-border bg-background p-3">
            <Label className="text-[12px]">Rate-limit policy</Label>
            <div className="mt-2 flex items-center gap-2">
              <Switch
                checked={Boolean(robots.bot_rate_limit.enabled)}
                onCheckedChange={(v) => setRobots({ ...robots, bot_rate_limit: { ...robots.bot_rate_limit, enabled: v } })}
                data-testid="seo-bot-ratelimit-switch"
              />
              <span className="text-[12px]">Enabled</span>
            </div>
            <div className="mt-3">
              <Label className="text-[11px]">Requests per minute (per UA / IP)</Label>
              <Input
                type="number"
                min={1}
                max={600}
                value={robots.bot_rate_limit.requests_per_minute}
                onChange={(e) => setRobots({
                  ...robots,
                  bot_rate_limit: {
                    ...robots.bot_rate_limit,
                    requests_per_minute: Math.max(1, Math.min(600, parseInt(e.target.value || '30', 10))),
                  },
                })}
                className="mt-1 h-8 max-w-[160px] font-mono-stat text-[12px]"
                data-testid="seo-bot-ratelimit-rpm"
              />
              <p className="mt-1 text-[10.5px] text-muted-foreground">Capped between 1 and 600 to prevent accidental DoS-on-self.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
